import styled from '@emotion/styled'
import { ReactElement } from 'react'

interface TimeSlotTagProps {
  /**
   * e.g. ['09:00', '-', '10:30'] or ['Main']
   */
  content: (string | ReactElement)[]
}

/**
 * Not rendering the content with the children prop because this element demands a very flexible style that changes the
 * layout based on the width of the component. The content prop can be ['Main'] or ['09:00', '-', '10:30'] if it is the
 * last the dash has to be styled differently from the others, see the styled component for more information.
 * */
const TimeSlotTag = ({ content }: TimeSlotTagProps) => {
  return (
    <StyledTimeSlotTag>
      {content.map(item => (
        <StyledTimeSlotTagContent key={item.toString()}>
          <span>{item}</span>
        </StyledTimeSlotTagContent>
      ))}
    </StyledTimeSlotTag>
  )
}

export default TimeSlotTag

const StyledTimeSlotTag = styled.div(({ theme }) => ({
  position: 'relative',
  zIndex: 3,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.space[1],
  color: theme.colors.white,
  background: theme.colors.timeSlot.tag.neutral,
  borderRadius: theme.radii[2],
  ...theme.fontStyle.p2,
  /**
   * The text is inside a span tag because a flex container cannot contain a ::first-letter pseudo-element.
   */
  span: { ...theme.mixins.capitalizeFirstLetter() },
  '[aria-checked="true"] &': {
    background: theme.colors.timeSlot.tag.active
  },
  '[disabled] &': {
    background: theme.colors.timeSlot.tag.disabled
  },
  /**
   * Meant for styling the dash between the times e.g. ['09:00', '-', '10:30'].
   * It is not possible to use media query to change StyledTimeSlotTag to [flex-direction = column] because
   * the component can't depend on the view port, it has to be flexible enough to know when it should display
   * like a column if the component is narrow. Position absolute is the best solution for this case.
   */
  '& > *:not(:only-child):nth-of-type(2)': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)'
  }
}))

const StyledTimeSlotTagContent = styled.span(({ theme }) => ({
  padding: theme.mixins.pxSpread([theme.space[1], theme.space[2]]),
  // override the lowercase transform
  span: {
    textTransform: 'none'
  }
}))
